import { usePublishedData } from "../lib/published-data";
import { createFileRoute, getRouteApi, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { DocumentContent } from "../components/document-content";
import { MatchupLibrary } from "../components/matchups/library";
import { creatorPageHead, shellFrom } from "../lib/head";
import { withDocumentStyles } from "../lib/document-route";
import { loadPage, useShell } from "../lib/document-route";
import { rememberLibrary } from "../lib/matchup-library";
import type { MatchupAppFilters } from "../../src/lib/matchup-app";
import { keepSearch, searchText, type RawSearch } from "../lib/search";

const matchups = getRouteApi("/matchups");

/** A filter that is set; an empty one stays out of the address. */
const set = (value: unknown) => (typeof value === "string" && value ? value : undefined);

/** Matchups (/matchups): the page the creator composed, or the library of guides. */
export const Route = createFileRoute("/matchups/")({
  // The address stays as written; the library reads its filters from it.
  validateSearch: keepSearch,
  loader: async ({ parentMatchPromise }) => {
    const { loaderData } = await parentMatchPromise;
    // A composed index places the library among the creator's own sections.
    return loaderData?.composedIndex
      ? loadPage("/matchups", "native")
      : ({ kind: "native" } as const);
  },
  staleTime: Infinity,
  head: ({ matches, loaderData }) => {
    const shell = shellFrom(matches);
    const library = (matches as readonly { routeId: string; loaderData?: unknown }[]).find(
      (match) => match.routeId === "/matchups",
    )?.loaderData as { header: { navTitle: string; intro: string; mainName: string } } | undefined;
    if (!shell) return {};
    if (loaderData?.kind === "page")
      return withDocumentStyles(
        creatorPageHead(shell, {
          pathname: "/matchups",
          title: loaderData.page.seoTitle || loaderData.page.title,
          description: loaderData.page.description,
          noindex: loaderData.private || loaderData.locked || loaderData.page.hideFromSearch,
          preloadImage: loaderData.lead,
        }),
        loaderData.document,
      );
    return creatorPageHead(shell, {
      pathname: "/matchups",
      title: `${library?.header.navTitle ?? "Matchups"} | ${shell.creator.name}`,
      description:
        library?.header.intro ||
        `${library?.header.mainName || "League"} matchup guides by ${shell.creator.name}`,
    });
  },
  component: MatchupsIndex,
});

function MatchupsIndex() {
  const initial = Route.useLoaderData();
  const library = matchups.useLoaderData();
  const search = Route.useSearch();
  const shell = useShell();
  const { data } = usePublishedData(initial, shell, "/matchups", "page");
  const navigate = useNavigate({ from: "/matchups/" });
  const filters: MatchupAppFilters = {
    q: searchText(search, "q") ?? "",
    difficulty: searchText(search, "difficulty") ?? "",
    role: searchText(search, "role") ?? "",
  };
  const href = `/matchups${new URLSearchParams(
    Object.entries({ ...filters, preview: searchText(search, "preview") ?? "" }).filter(
      ([, value]) => value,
    ),
  )
    .toString()
    .replace(/^(?=.)/, "?")}`;
  useEffect(() => rememberLibrary(href), [href]);
  if (library.kind === "pages")
    return data.kind === "page" ? <DocumentContent data={data} shell={shell} /> : null;
  const content = (
    <MatchupLibrary
      library={library}
      filters={filters}
      href={href}
      onFilters={(next) =>
        void navigate({
          search: (previous: RawSearch) => ({
            ...previous,
            q: set(next.q),
            difficulty: set(next.difficulty),
            role: set(next.role),
          }),
          replace: true,
          resetScroll: false,
        })
      }
      onOpen={(entry) =>
        void navigate({ href: entry.href ?? `/matchups/${entry.slug}` } as Parameters<
          typeof navigate
        >[0])
      }
    />
  );
  // A composed index places the library at its built-in block, among the creator's sections.
  return data.kind === "page" ? (
    <DocumentContent data={data} shell={shell} builtIn={content} />
  ) : (
    content
  );
}
